import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { FullscreenTakeover, QueryLoader, Step as StepType, StepIndicator } from "@jsq/ds/core";
import { StepNames } from "./constants";
import { PaymentBatchActionTypes } from "./Context/constants";
import {
  paymentBatchDataReducer,
  initialPaymentBatchState,
  PaymentBatchDataContextProvider,
  PaymentBatchDispatchProvider,
} from "./Context/context";
import { useAccountingPayable } from "./hooks/useAccountingPayable";
import CompleteStep from "./StepComponents/CompleteStep";
import ErrorStep from "./StepComponents/ErrorStep";
import PaymentDetailsStep from "./StepComponents/PaymentDetailsStep";
import ReviewAndSubmitStep from "./StepComponents/ReviewAndSubmitStep";
import SelectPositionsStep from "./StepComponents/SelectPositionsStep";

export interface PaymentBatchWorkflowProps {
  batchId: string;
  batchObjectType?: string;
  onClose: () => void;
  onComplete?: (paymentBatchId: string) => void;
  clientName?: string;
  investmentEntityName?: string;
  initialStep?: StepNames;
}

export const PaymentBatchWorkflow: React.FC<PaymentBatchWorkflowProps> = ({
  batchId: initialBatchId,
  batchObjectType: initialBatchObjectType = "",
  onClose,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- onComplete will be wired up in the ReviewAndSubmitStep implementation (FF-6674 Task 4)
  onComplete,
  clientName: initialClientName = "",
  investmentEntityName: initialInvestmentEntityName = "",
  initialStep = StepNames.SelectPositions,
}: PaymentBatchWorkflowProps) => {
  const [state, dispatch] = useReducer(paymentBatchDataReducer, {
    ...initialPaymentBatchState,
    clientName: initialClientName,
    investmentEntityName: initialInvestmentEntityName,
    batchId: initialBatchId,
    batchObjectType: initialBatchObjectType,
    isBatchOfOne: initialStep === StepNames.PaymentDetails,
  });

  // Data fetching — query runs once and results flow into context
  const { positionRows, queryData, queryResult } = useAccountingPayable({
    batchId: initialBatchId,
  });

  const [currentStep, setCurrentStep] = useState<StepNames>(initialStep);

  // Process query data, populate missing meta, and recover batch-of-1 behavior on refresh.
  // When payeeCount === 1 and the state was not pre-marked as batch-of-one via initialStep,
  // flag isBatchOfOne and auto-select the single position. The existing effectiveStep
  // derivation then synchronously redirects SelectPositions → PaymentDetails.
  const hasProcessedQuery = useRef(false);
  useEffect(() => {
    if (!hasProcessedQuery.current && queryData && positionRows.length > 0) {
      hasProcessedQuery.current = true;

      const shouldRecoverBatchOfOne = !state.isBatchOfOne && queryData.payeeCount === 1;
      const isBatchOfOne = state.isBatchOfOne || shouldRecoverBatchOfOne;

      dispatch({
        type: PaymentBatchActionTypes.SET_BATCH_DATA,
        payload: { batchData: queryData },
      });

      dispatch({
        type: PaymentBatchActionTypes.SET_SELECT_POSITIONS_DATA,
        payload: {
          clientName: queryData.arena?.name || state.clientName,
          investmentEntityName: queryData.entity?.name || state.investmentEntityName,
          batchObjectType: queryData.objectType || state.batchObjectType,
          ...(isBatchOfOne
            ? {
                selectedPositions: positionRows,
                numberOfAvailablePositions: positionRows.filter((r) => r.isSelectable).length,
              }
            : {}),
        },
      });

      if (shouldRecoverBatchOfOne) {
        dispatch({ type: PaymentBatchActionTypes.SET_BATCH_OF_ONE });
      }
    }
  }, [
    queryData,
    positionRows,
    dispatch,
    state.isBatchOfOne,
    state.clientName,
    state.investmentEntityName,
    state.batchObjectType,
  ]);

  const onSelectPositionsBack = useCallback(() => {
    dispatch({
      type: PaymentBatchActionTypes.SET_SELECTED_POSITIONS,
      payload: { selectedPositions: [] },
    });
    onClose();
  }, [onClose]);

  const onSelectPositionsContinue = useCallback(() => {
    setCurrentStep(StepNames.PaymentDetails);
  }, []);

  const onPaymentDetailsBack = useCallback(() => {
    if (state.isBatchOfOne) {
      onClose();
    } else {
      setCurrentStep(StepNames.SelectPositions);
    }
  }, [state.isBatchOfOne, onClose]);

  const onPaymentDetailsContinue = useCallback(() => {
    setCurrentStep(StepNames.ReviewAndSubmit);
  }, []);

  const onReviewAndSubmitBack = useCallback(() => {
    setCurrentStep(StepNames.PaymentDetails);
  }, []);

  const onReviewAndSubmitSubmit = useCallback((error?: Error) => {
    if (error) {
      dispatch({
        type: PaymentBatchActionTypes.SET_ERROR_MESSAGE,
        payload: { errorMessage: error.message },
      });
      setCurrentStep(StepNames.Error);
    } else {
      setCurrentStep(StepNames.Complete);
    }
  }, []);

  const steps = useMemo(() => {
    const allSteps = [
      {
        name: StepNames.SelectPositions,
        component: SelectPositionsStep,
        onBack: onSelectPositionsBack,
        onContinue: onSelectPositionsContinue,
      },
      {
        name: StepNames.PaymentDetails,
        component: PaymentDetailsStep,
        onBack: onPaymentDetailsBack,
        onContinue: onPaymentDetailsContinue,
      },
      {
        name: StepNames.ReviewAndSubmit,
        component: ReviewAndSubmitStep,
        onBack: onReviewAndSubmitBack,
        onContinue: onReviewAndSubmitSubmit,
      },
      {
        name: StepNames.Complete,
        component: CompleteStep,
        onBack: () => {},
        onContinue: () => {},
      },
      {
        name: StepNames.Error,
        component: ErrorStep,
        onBack: () => {},
        onContinue: () => {},
      },
    ];

    // Batch of 1: hide SelectPositions from step indicator when auto-detected
    const filteredSteps = state.isBatchOfOne
      ? allSteps.filter((s) => s.name !== StepNames.SelectPositions)
      : allSteps;

    return filteredSteps.map((step) => ({
      ...step,
      id: step.name.toLowerCase().split(" ").join("_"),
    }));
  }, [
    state.isBatchOfOne,
    onPaymentDetailsBack,
    onPaymentDetailsContinue,
    onReviewAndSubmitBack,
    onReviewAndSubmitSubmit,
    onSelectPositionsBack,
    onSelectPositionsContinue,
  ]);

  // Derive the effective step synchronously — no useEffect delay.
  // When batch-of-1 is detected, SelectPositions is filtered from the steps array,
  // so we need to redirect to PaymentDetails in the same render frame.
  const effectiveStep =
    state.isBatchOfOne && currentStep === StepNames.SelectPositions
      ? StepNames.PaymentDetails
      : currentStep;

  const currentStepIndex = steps.findIndex((step) => step.name === effectiveStep);

  const getHeaderStepStatus = useMemo(() => {
    return (stepIndex: number): StepType["status"] => {
      if (stepIndex === currentStepIndex) {
        return "active";
      } else if (stepIndex < currentStepIndex) {
        return "complete";
      }
      return "inactive";
    };
  }, [currentStepIndex]);

  const headerSteps: StepType[] = useMemo(
    () =>
      steps.map((step, index) => ({
        index,
        name: step.name,
        url: step.id,
        status: getHeaderStepStatus(index),
      })),
    [getHeaderStepStatus, steps],
  );

  const indicatorSteps: StepType[] = useMemo(
    () =>
      headerSteps.filter(
        (s) => ![StepNames.Complete, StepNames.Error].includes(s.name as StepNames),
      ),
    [headerSteps],
  );

  const currentStepDetails = useMemo(
    () => steps[currentStepIndex] ?? steps[0],
    [currentStepIndex, steps],
  );
  const StepComponent = currentStepDetails.component;

  const headerTitle = useMemo(() => {
    const label = state.batchData?.label;
    const subtitleLabel = state.batchData?.subtitleLabel;
    if (!label) return "Create new payment";
    const transactionType = subtitleLabel ? `${label} (${subtitleLabel})` : label;
    return `Create new payment · ${transactionType}`;
  }, [state.batchData?.label, state.batchData?.subtitleLabel]);

  return (
    <PaymentBatchDataContextProvider value={state}>
      <PaymentBatchDispatchProvider value={dispatch}>
        <FullscreenTakeover>
          <FullscreenTakeover.Header title={headerTitle} onExit={onClose} />
          <FullscreenTakeover.Subheader>
            <StepIndicator disableStepLinks steps={indicatorSteps} />
          </FullscreenTakeover.Subheader>
          <FullscreenTakeover.Body>
            <QueryLoader result={queryResult}>
              <StepComponent
                onBack={currentStepDetails.onBack}
                onContinue={currentStepDetails.onContinue}
              />
            </QueryLoader>
          </FullscreenTakeover.Body>
        </FullscreenTakeover>
      </PaymentBatchDispatchProvider>
    </PaymentBatchDataContextProvider>
  );
};

export default PaymentBatchWorkflow;
